import { useState } from "react";
import axios from "axios";
import { X, Check, Bell } from "lucide-react";
import { API } from "../utils/api";

const SignUpModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    preferences: {
      new_releases: true,
      ppv_events: true,
      live_streams: false,
      weekly_digest: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Build preferences string
    const prefs = Object.entries(formData.preferences)
      .filter(([_, v]) => v)
      .map(([k]) => k)
      .join(',');

    try {
      const response = await axios.post(`${API}/subscribers`, null, {
        params: {
          email: formData.email,
          name: formData.name,
          preferences: prefs
        }
      });

      if (response.data.success) {
        setSuccess(true);
        onSuccess && onSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="signup-success-modal">
        <div className="bg-gray-900 rounded-xl w-full max-w-md p-8 text-center">
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">You're In!</h2>
          <p className="text-gray-400 mb-6">
            Welcome to CBXTV! We'll send you notifications about new releases and exclusive content.
          </p>
          <button
            onClick={onClose}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-medium w-full"
          >
            Start Watching
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="signup-modal">
      <div className="bg-gray-900 rounded-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="relative h-32 bg-gradient-to-r from-red-600 to-red-800">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X size={24} />
          </button>
          <div className="absolute bottom-4 left-6">
            <h2 className="text-2xl font-bold text-white">Join CBXTV</h2>
            <p className="text-white/80 text-sm">Get notified about new releases</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-red-600 focus:outline-none"
              placeholder="your@email.com"
              required
              data-testid="signup-email"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Your Name (optional)</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-red-600 focus:outline-none"
              placeholder="John Doe"
              data-testid="signup-name"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-3">Notify me about:</label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.preferences.new_releases}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: {...formData.preferences, new_releases: e.target.checked}
                  })}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-red-600 focus:ring-red-600"
                />
                <div>
                  <span className="text-white group-hover:text-red-400 transition-colors">New Releases</span>
                  <p className="text-gray-500 text-xs">Movies, TV shows & exclusive content</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.preferences.ppv_events}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: {...formData.preferences, ppv_events: e.target.checked}
                  })}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-red-600 focus:ring-red-600"
                />
                <div>
                  <span className="text-white group-hover:text-red-400 transition-colors">PPV Events</span>
                  <p className="text-gray-500 text-xs">Cheater Beaters, Incognito & more</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.preferences.live_streams}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: {...formData.preferences, live_streams: e.target.checked}
                  })}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-red-600 focus:ring-red-600"
                />
                <div>
                  <span className="text-white group-hover:text-red-400 transition-colors">Live Streams</span>
                  <p className="text-gray-500 text-xs">When we go live on any channel</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.preferences.weekly_digest}
                  onChange={(e) => setFormData({
                    ...formData,
                    preferences: {...formData.preferences, weekly_digest: e.target.checked}
                  })}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-red-600 focus:ring-red-600"
                />
                <div>
                  <span className="text-white group-hover:text-red-400 transition-colors">Weekly Digest</span>
                  <p className="text-gray-500 text-xs">Best of the week every Sunday</p>
                </div>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-700 text-white py-3 rounded-lg font-bold text-lg transition-colors"
            data-testid="signup-submit"
          >
            {loading ? 'Subscribing...' : 'Subscribe for Free'}
          </button>

          <p className="text-gray-500 text-xs text-center">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUpModal;
